import {
    FetchState,
    HsaBankStatementState,
    HsaBankStatementsList,
    HsaBankStatementListItem,
    GeneratedPdfState,
    AnonDocumentState,
    AnonDocumentKey,
    UserFeedbackEventAction,
    HsabDocumentType
} from "@/shared/types";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import Vue from "vue";
import {
    AppBrowserTarget,
    isMobileApp,
    openUrl
} from "@/shared/mobileHelper";
import bankResource from "@/shared/resource/bank";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";

/*
  I'm assuming that the lookbackWindow parameter is the number of months in the past to search.
  This assumption is being made before the GetStatementList is available for testing, so the logic around
  whether a page is fully loaded and how the lookbackWindow value is passed may need to be updated.
*/

const currentYear = moment().year();
const currentMonth = moment().month() + 1; // moment is 0-indexed, so add 1

function numberOfMonthsAgoToStartOfYear (year: number) {
    if (year > currentYear) {
        return 1;
    }

    return currentMonth + (12 * (currentYear - year));
}

const isStatementForYear = (year: number) => (s: HsaBankStatementListItem) => {
    return moment(s.datePeriodEnd).year() === year || moment(s.datePeriodStart).year() === year;
};

export const initialState: HsaBankStatementState = {
    year: 0,
    recentLookbackWindow: 0,
    fetchState: FetchState.Uninitialized,
    list: {
        statements: []
    },
    pdfs: {},
    anonDocuments: {},
};

export const useHsaBankStatements = defineStore("hsaBankStatements", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        monthlyStatementsForYear: state => state.list.statements.filter(isStatementForYear(state.year)),
        isPageStatementsLoaded () {
            return this.recentLookbackWindow >= numberOfMonthsAgoToStartOfYear(this.year) &&
                this.fetchState === FetchState.Loaded;
        },

        isMonthlyStatementsForYear: state => (year: number) =>
            state.list.statements.filter(isStatementForYear(year)).length > 0,

        canViewNextYearStatements: state => state.year < currentYear,

        canViewPreviousYearStatements (state) {
            return this.isPageStatementsLoaded && this.isMonthlyStatementsForYear(state.year - 1);
        },

        monthlyStatementPdfs: state => state.pdfs,
        monthlyStatementLocations: state => state.anonDocuments,

        // this will not work for reactivity, but useful in Actions
        pdfFetchStateUnreactive: state => (identifier: string) => state.pdfs[identifier]
            ? state.pdfs[identifier].fetchState
            : FetchState.Uninitialized,

        // this will not work for reactivity, but useful in Actions
        anonDocumentFetchStateUnreactive: state => (identifer: string) => state.anonDocuments[identifer]
            ? state.anonDocuments[identifer].fetchState
            : FetchState.Uninitialized,
    },
    actions: {
        setYear (year: number) {
            this.year = year;
        },
        setFetchState ({ lookbackWindow, fetchState }: { lookbackWindow: number, fetchState: FetchState }) {
            this.recentLookbackWindow = lookbackWindow;
            this.fetchState = fetchState;
        },
        setStatements (listing: HsaBankStatementsList) {
            if (!listing.statements) {
                listing.statements = [];
            }

            this.fetchState = FetchState.Loaded;
            this.list = listing;
            // initialize pdfs state object
            listing.statements.forEach(s => {
                const pdfState = this.pdfs[s.identifier];
                if (pdfState) {
                    return;
                } // don't need to intilize it

                Vue.set(this.pdfs, s.identifier, emptyGeneratedPdfState());
                Vue.set(this.anonDocuments, s.identifier, emptyAnonDocumentState());
            });
        },

        setStatementPdfFetchState ([ identifier, fetchState ]: [ string, FetchState ]) {
            storeHelper.vueSet(this.pdfs[identifier], "fetchState", fetchState);
        },

        setStatementPdf ({ identifier, pdfData }: { identifier: string, pdfData: Blob }) {
            storeHelper.vueSet(this.pdfs[identifier], "fetchState", FetchState.Loaded);
            storeHelper.vueSet(this.pdfs[identifier], "pdfData", pdfData);
        },

        setAnonDocumentFetchState ([ identifier, fetchState ]: [ string, FetchState ]) {
            storeHelper.vueSet(this.anonDocuments[identifier], "fetchState", fetchState);
        },

        setAnonDocumentKey ({ identifier, documentKey }: { identifier: string, documentKey: AnonDocumentKey }) {
            storeHelper.vueSet(this.anonDocuments[identifier], "fetchState", FetchState.Loaded);
            storeHelper.vueSet(this.anonDocuments[identifier], "documentKey", documentKey);
        },
        loadStatements (lookbackWindow: number): Promise<void> {
            if ((lookbackWindow <= this.recentLookbackWindow && this.fetchState !== FetchState.Uninitialized)) {
                return Promise.resolve();
            }

            this.setFetchState({ lookbackWindow, fetchState: FetchState.Loading });
            const notifierStore = useNotifierStore();
            return BankService.getHsaBankStatements(lookbackWindow)
                .then(response => {
                    this.setStatements(response.data);
                }).catch(err => {
                    this.setFetchState({ lookbackWindow, fetchState: FetchState.Loaded });
                    const errorMessageForUser = "There was an error retrieving monthly statements.";
                    notifierStore.setDanger(errorMessageForUser);
                    return Promise.resolve();
                });
        },
        loadCombinedStatements (lookbackWindow: number): Promise<void> {
            if ((lookbackWindow <= this.recentLookbackWindow && this.fetchState !== FetchState.Uninitialized)) {
                return Promise.resolve();
            }

            this.setFetchState({ lookbackWindow, fetchState: FetchState.Loading });

            return BankService.getHsaBankCombinedStatements(lookbackWindow)
                .then(response => {
                    this.setStatements(response.data);
                }).catch(() => {
                    const notifierStore = useNotifierStore();
                    this.setFetchState({ lookbackWindow, fetchState: FetchState.Loaded });
                    const errorMessageForUser = "There was an error retrieving monthly statements.";
                    notifierStore.setDanger(errorMessageForUser);
                    return Promise.resolve();
                });
        },
        loadStatementsForPage (year: number): Promise<any> {
            this.setYear(year);
            const previousYear = year - 1;
            return this.loadCombinedStatements(numberOfMonthsAgoToStartOfYear(previousYear));
        },

        openNotionalStatementLocation (
            { identifier, linkTitle, docType }: { identifier: string, linkTitle: string, docType: HsabDocumentType }
        ): Promise<any> {
            if (this.anonDocumentFetchStateUnreactive(identifier) === FetchState.Loaded) {
                return this.goToAnonDocument(identifier);
            }

            if (
                this.anonDocumentFetchStateUnreactive(identifier) !== FetchState.Uninitialized
            ) {
                return Promise.resolve();
            }
            const notifierStore = useNotifierStore();
            this.setAnonDocumentFetchState([ identifier, FetchState.Loading ]);
            return BankService.getHsaBankNotionalStatementKey(identifier)
                .then(response => {
                    this.setAnonDocumentKey({
                        identifier,
                        documentKey: response.data
                    });

                    return true;
                })
                .catch(() => {
                    this.setAnonDocumentFetchState([ identifier, FetchState.Uninitialized ]);
                    const errorMessageForUser = `There was an error retrieving ${linkTitle}`;
                    notifierStore.setDanger(errorMessageForUser);
                    return Promise.resolve(false);
                })
                .then(success => {
                    if (success) {
                        return this.goToAnonDocument(identifier);
                    }
                });
        },
        openStatementLocation (
            { identifier, linkTitle, docType }: { identifier: string, linkTitle: string, docType: HsabDocumentType }
        ): Promise<any> {
            if (this.anonDocumentFetchStateUnreactive(identifier) === FetchState.Loaded) {
                return this.goToAnonDocument(identifier);
            }

            if (
                this.anonDocumentFetchStateUnreactive(identifier) !== FetchState.Uninitialized
            ) {
                return Promise.resolve();
            }
            const notifierStore = useNotifierStore();
            this.setAnonDocumentFetchState([ identifier, FetchState.Loading ]);
            return BankService.getHsaBankStatementKey(identifier)
                .then(response => {
                    this.setAnonDocumentKey({
                        identifier,
                        documentKey: response.data
                    });

                    return true;
                })
                .catch(() => {
                    this.setAnonDocumentFetchState([ identifier, FetchState.Uninitialized ]);
                    const errorMessageForUser = `There was an error retrieving ${linkTitle}`;
                    notifierStore.setDanger(errorMessageForUser);
                    return Promise.resolve(false);
                })
                .then(success => {
                    if (success) {
                        return this.goToAnonDocument(identifier);
                    }
                });
        },

        goToAnonDocument (identifier: string): Promise<any> {
            const url = bankResource.hsaBankAnonDocument(this.anonDocuments[identifier].documentKey);
            this.setAnonDocumentFetchState([ identifier, FetchState.Uninitialized ]);
            if (isMobileApp()) {
                openUrl(url, AppBrowserTarget.system);
            } else {
                window.open(url, "_blank");
            }
            const userFeedbackStore = useUserFeedbackStore();
            userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.AccountSummary);
            return Promise.resolve();
        }
    }
});

function emptyGeneratedPdfState (): GeneratedPdfState {
    return {
        fetchState: FetchState.Uninitialized,
        pdfData: null
    };
}

function emptyAnonDocumentState (): AnonDocumentState {
    return {
        fetchState: FetchState.Uninitialized,
        documentKey: null
    };
}